import { CheckIcon } from "lucide-react";

import { PictureAsset } from "@/components/common/picture-asset";
import { cn } from "@/lib/utils";
import { resolvePlurals } from "@/service/text/plurals";

// Grafiki banera z CDN (bez rozszerzenia — PictureAsset dokleja .avif/.png).
// Tło jest w proporcji 1196x164, postać z chipami uczestników 1172x657.
export const BANNER_BACKGROUND =
  "https://cdn.remedium.md/image/common/2026/07/29/15/km-baner-1-png-6a6a029684f75";
export const BANNER_PERSON =
  "https://cdn.remedium.md/image/common/2026/07/29/15/ziom-png-6a6a02dc76fa6";

// kolory awatarów uczestników wprost z projektu — trzy pierwsze inicjały w stosie
const PARTICIPANT_AVATAR_COLORS = [
  "bg-[#8812af]",
  "bg-[#06a8bf]",
  "bg-[#1b35c6]",
];

const AVATAR_PREVIEW_COUNT = 3;

// forma „few" (2-4) polszczyzny — resolvePlurals odmienia rzeczownik, czasownik trzeba osobno
const isFewForm = (count: number) =>
  count % 10 > 1 && count % 10 < 5 && !(count % 100 > 10 && count % 100 < 20);

// Otwarta runda mówi o tym, co dzieje się teraz: „5 osób rozpoczęło podejście".
const resolveStartedAttemptVerb = (count: number) => {
  if (1 === count) {
    return "rozpoczęła podejście";
  }

  return isFewForm(count) ? "rozpoczęły podejście" : "rozpoczęło podejście";
};

// Poza otwartą rundą backend podaje liczbę podejść z całej edycji, a nie liczbę osób — stąd
// „792 rozwiązanych egzaminów" zamiast liczenia uczestników. Forma dopełniacza także dla 2-4
// („22 rozwiązanych egzaminów"), bez wariantu mianownikowego.
const resolveSolvedExamsNoun = (count: number) =>
  1 === count ? "rozwiązany egzamin" : "rozwiązanych egzaminów";

/**
 * Stos awatarów uczestników + „323 osoby biorą udział". W projekcie desktopowym siedzi nad
 * banerem, a w karcie mobilnej pod przyciskiem akcji — stąd wspólny komponent.
 *
 * `isRoundActive` = trwa runda: liczba dotyczy bieżącej rundy i pokazujemy awatary. Poza rundą
 * (oczekiwanie na kolejną albo koniec edycji) backend podaje sumę podejść z całej edycji, tekst
 * idzie w czas przeszły, a awatarów nie ma — nie reprezentowałyby tej liczby.
 */
export const PracticeExamParticipantsSummary = ({
  participantCount,
  participantInitials,
  isRoundActive = false,
  className,
}: {
  participantCount: number;
  participantInitials: string[];
  isRoundActive?: boolean;
  className?: string;
}) => {
  if (participantCount < 1) {
    return null;
  }

  // backend potrafi dać kilka znaków („TB") — w kółku 20 px mieści się tylko jedna litera
  const avatars = participantInitials
    .slice(0, AVATAR_PREVIEW_COUNT)
    .map((initial) => initial.trim().charAt(0).toUpperCase());

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* awatary tylko przy otwartej rundzie — poza nią liczba dotyczy całej edycji */}
      {isRoundActive && avatars.length > 0 && (
        <div className="flex items-center">
          {avatars.map((initial, index) => (
            <span
              key={`${initial}-${index}`}
              className={cn(
                // Biała obwódka jako ring (box-shadow), nie border: przy `border` przeglądarka
                // rysuje dwie zaokrąglone krawędzie (tła i obwódki) i antyaliasing na kole 20 px
                // wychodzi poszarpany. Box-shadow ma jedną krawędź i wygładza się czysto.
                "flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] leading-none font-semibold text-white ring-1 ring-white",
                PARTICIPANT_AVATAR_COLORS[
                  index % PARTICIPANT_AVATAR_COLORS.length
                ],
                index < avatars.length - 1 && "-mr-1.5",
              )}
            >
              {initial}
            </span>
          ))}
        </div>
      )}
      <p className="text-muted-foreground min-w-0 truncate text-sm">
        {isRoundActive ? (
          <>
            <span className="font-bold">
              {resolvePlurals(participantCount, "osoba", "osób", "osoby")}
            </span>{" "}
            {resolveStartedAttemptVerb(participantCount)}
          </>
        ) : (
          <>
            <span className="font-bold">{participantCount}</span>{" "}
            {resolveSolvedExamsNoun(participantCount)}
          </>
        )}
      </p>
    </div>
  );
};

/**
 * Wspólna skorupa banera próbnego LEK-u: pasek nagłówka („Próbny LEK" + uczestnicy), tło, postać
 * i układ treści. Używają jej oba stany strony — baner rundy (z odliczaniem) i baner po rozliczeniu
 * wszystkich rund — żeby grafiki i geometria nie rozjechały się między nimi.
 */
export const PracticeExamBannerShell = ({
  children,
  participantCount,
  participantInitials,
  isRoundActive = false,
  shiftPersonLeft = false,
}: {
  children: React.ReactNode;
  participantCount: number;
  participantInitials: string[];
  isRoundActive?: boolean;
  // baner z odliczaniem ma po prawej o jeden blok więcej — postać z chipami schodzi wtedy z centrum
  // w lewo, żeby jej chipy nie wchodziły pod etykietę licznika
  shiftPersonLeft?: boolean;
}) => (
  // Odstęp nad banerem daje już layout: poniżej xl breadcrumby mają `mb-6`, od xl treść dostaje
  // `xl:py-10`. Dlatego bez własnego marginesu u góry, a od xl jeszcze podciągnięcie o 24 px.
  <div className="mb-6 xl:-mt-6">
    {/* pasek nad banerem z projektu: nazwa sekcji po lewej, uczestnicy po prawej */}
    <div className="mb-4 flex items-center justify-between gap-4">
      <h2 className="text-lg font-semibold tracking-[-0.4px] text-zinc-900">
        Próbny LEK
      </h2>
      <PracticeExamParticipantsSummary
        participantCount={participantCount}
        participantInitials={participantInitials}
        isRoundActive={isRoundActive}
      />
    </div>
    <div className="from-remedium-blue to-remedium-blue-light relative overflow-hidden rounded-2xl bg-gradient-to-r px-6 py-6 md:px-10 md:py-8">
      {/* tło banera; gradient pod spodem zostaje jako fallback, gdy grafika się nie wczyta */}
      <PictureAsset
        source={BANNER_BACKGROUND}
        ext="png"
        alt=""
        wrapperClassName="pointer-events-none absolute inset-0"
        className="h-full w-full object-cover"
      />
      {/* Postać z chipami uczestników — dekoracja. Wystaje 9% poniżej dolnej krawędzi (obcina ją
        overflow-hidden), tak jak w projekcie. Poniżej xl kolidowałaby z tekstem i CTA. */}
      <PictureAsset
        source={BANNER_PERSON}
        ext="png"
        alt=""
        wrapperClassName={cn(
          "pointer-events-none absolute top-[9%] hidden h-full -translate-x-1/2 xl:block",
          shiftPersonLeft ? "left-[48%]" : "left-1/2",
        )}
        className="h-full w-auto max-w-none"
      />
      <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        {children}
      </div>
    </div>
  </div>
);

/**
 * Krokowy wskaźnik rund (1—2—3—4). Rundy rozliczone dostają zielony znacznik z „fajką" zamiast
 * numeru, bieżąca zostaje wyróżniona, kolejne są przygaszone. `currentRound === null` oznacza
 * edycję rozliczoną — wtedy każda runda jest odhaczona.
 *
 * `tone` dobiera kolory do podłoża: „onColor" na granatowym banerze, „onLight" na białej karcie
 * (wariant mobilny), gdzie biel byłaby niewidoczna.
 */
export const PracticeExamRoundSteps = ({
  rounds,
  currentRound,
  tone = "onColor",
  className,
}: {
  rounds: number;
  currentRound: number | null;
  tone?: "onColor" | "onLight";
  className?: string;
}) => {
  if (rounds < 2) {
    return null;
  }

  const isOnLight = "onLight" === tone;

  return (
    <div
      className={cn("flex items-center", isOnLight ? "" : "mt-5", className)}
    >
      {Array.from({ length: rounds }, (_, index) => index + 1).map((round) => {
        // brak bieżącej rundy = cała edycja rozliczona, więc każdy krok jest odhaczony
        const isFinished = null === currentRound || round < currentRound;
        const isCurrent = null !== currentRound && round === currentRound;

        return (
          <div key={round} className="flex items-center">
            {round > 1 && (
              // Łącznik jest neutralny (nie zielony) i nie dotyka kółek — w projekcie ma z obu
              // stron prześwit, dlatego marginesy poziome zamiast stykania się z krawędzią.
              <span
                className={cn(
                  "mx-1.5 h-px",
                  isOnLight ? "bg-remedium-blue/20 w-5" : "w-4 bg-white/40",
                )}
              />
            )}
            <span
              className={cn(
                "flex size-6 items-center justify-center rounded-full text-xs font-medium",
                isFinished &&
                  cn(
                    "bg-teal-400 text-white",
                    isOnLight
                      ? "ring-2 ring-teal-400/20 ring-offset-1 ring-offset-white"
                      : "ring-2 ring-white/25",
                  ),
                !isFinished &&
                  isCurrent &&
                  (isOnLight
                    ? "bg-remedium-blue ring-remedium-blue/20 text-white ring-2 ring-offset-1 ring-offset-white"
                    : "text-remedium-blue bg-white font-semibold"),
                !isFinished &&
                  !isCurrent &&
                  (isOnLight
                    ? "bg-remedium-blue/20 border-remedium-blue/40 text-remedium-blue border-[1.3px]"
                    : "bg-white/15 font-semibold text-white/70"),
              )}
            >
              {isFinished ? (
                <CheckIcon size={14} className="stroke-[3]" />
              ) : (
                round
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};
